/**
 * @fileoverview Full-page "something went wrong" state for a sales page whose data failed to load.
 * @description Replaces the old one-line red error text with a centered state card: a red alert
 * icon, a "Something went wrong" title, the page's own load-error sentence as the explanation, and
 * a "Try again" button that refetches the page's queries in place (no full reload, so filters and
 * scroll position survive). Rendered under the page header, so the person still sees where they are.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import Button from '@/shared/components/atoms/Button'
import StateMessage from '@/shared/components/molecules/StateMessage'

import type { Query } from '@tanstack/react-query'

interface PageLoadErrorProps {
  message: string
  testId?: string
}

/**
 * @description Whether a query is in the failed state.
 * @param {Query} query - A cached query.
 * @returns {boolean} True when its last fetch failed.
 */
function hasFailed(query: Query): boolean {
  return query.state.status === 'error'
}

/**
 * @component
 * @param {PageLoadErrorProps} props - The page's load-error sentence and optional test id.
 * @param {string} props.message - The page's own "Could not load X. Please try again." text,
 * shown as the card's explanation.
 * @param {string} [props.testId] - `data-testid` for the card, so QA can find each page's error.
 */
function PageLoadError({ message, testId }: PageLoadErrorProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  /**
   * @description Re-runs only the queries that failed, so Try again does not also refetch every
   * unrelated query that happens to be active (the session, notifications, other summaries).
   */
  const retry = () => {
    void queryClient.refetchQueries({ type: 'active', predicate: hasFailed })
  }

  return (
    <section className="flex min-h-state items-center justify-center rounded-card border border-chip-border bg-white">
      <StateMessage
        icon="alert"
        tone="bad"
        title={t('sales.pageError.title')}
        description={message}
        testId={testId}
        actions={
          <Button
            type="button"
            size="md"
            onClick={retry}
            data-testid="page-load-error-retry-button"
          >
            {t('sales.pageError.retry')}
          </Button>
        }
      />
    </section>
  )
}

export default PageLoadError
